import { NotaryActLocationPage } from "@/components/pages/NotaryActLocationPageTh";
// /notary/$act/in/$location — Notary act × Location combo (long-form 1,800+ words).
// Top 8 high-intent acts × all 130 locations ≈ 1,040 SEO landing pages.
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { NOTARY_ACTS, getNotaryAct, type NotaryAct } from "@/data/notary-acts";
import { getLocation, locations, type Location } from "@/data/locations";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { FaqAccordion, ContactCTA } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import { RelatedComparisons } from "@/components/RelatedComparisons";
import { RelatedServicesInLocation } from "@/components/RelatedServicesInLocation";
import { EntityPanel } from "@/components/EntityPanel";
import NotaryActDeepContent from "@/components/NotaryActDeepContent";
import {
  ScrollText,
  MapPin,
  FileCheck2,
  ShieldCheck,
  Clock,
  MessageCircle,
  Phone,
  BadgeCheck,
  ArrowRight,
  Building2,
  Landmark,
  Train,
} from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

// 8 top-search acts that justify a city-page combo.
export const TOP_NOTARY_ACT_SLUGS = [
  "affidavit",
  "power-of-attorney",
  "certified-true-copy",
  "jurat",
  "affidavit-single",
  "travel-consent",
  "certify-translation",
  "apostille-request",
  // Phase NOTARY-EXPAND++ — high-intent expansion
  "certify-signature",
  "statutory-declaration",
  "specific-poa",
  "copy-passport",
  "copy-education",
  "oath",
  "affidavit-support",
  "affidavit-name",
  // Phase 7C — Notary depth expansion (14 new acts → 30 total)
  "copy-id",
  "copy-house-registration",
  "copy-birth-cert",
  "copy-marriage-cert",
  "dbd-signature",
  "certify-signature-juristic",
  "affidavit-single-status",
  "certify-photo",
  "life-certificate",
  "proof-of-address",
  "certify-no-criminal",
  "certify-signing-authority",
  "corporate-resolution",
  "contract-property-foreigner",
] as const;

export function isTopNotaryAct(slug: string): boolean {
  return (TOP_NOTARY_ACT_SLUGS as readonly string[]).includes(slug);
}

export const Route = createFileRoute("/notary/$act/in/$location")({
  loader: async ({ params }) => {
    const { getDistrictContext } = await import("@/data/district-context");
    const { buildCombo } = await import("@/data/combo-content");
    if (!isTopNotaryAct(params.act)) throw notFound();
    if (!getNotaryAct(params.act)) throw notFound();
    if (!getLocation(params.location)) throw notFound();
    return { actSlug: params.act, locSlug: params.location };
  },
  head: ({ params }) => {
    const n = getNotaryAct(params.act);
    const loc = getLocation(params.location);
    if (!n || !loc)
      return {
        meta: [{ title: ctaTitle("Notary — ไม่พบหน้า", "/notary/$act/in/$location", "th") }],
      };
    const url = `${SITE}/notary/${n.slug}/in/${loc.slug}`;
    const shortLoc = loc.nameTh.replace("จังหวัด", "จ.").replace("เขต", "");
    const title = `${n.nameTh} ${shortLoc} ฿${n.priceFromTh.toLocaleString()} เสร็จ ${n.turnaround} | NYC Notary`;
    const desc = `บริการ ${n.nameTh} (${n.nameEn}) สำหรับลูกค้า${loc.nameTh} โดยทนาย Notarial Services ขึ้นทะเบียนสภาทนายความ · ${n.turnaround} · รับ-ส่งถึงพื้นที่ · LINE @nycli — ส่งภาพเอกสารให้เราตรวจก่อน ฟรี`;

    const faqs = [
      {
        q: `${n.nameTh} ที่${loc.nameTh} ราคาเท่าไหร่?`,
        a: `เริ่ม ฿${n.priceFromTh.toLocaleString()} โดยทนาย Notary Public ขึ้นทะเบียนสภาทนายความฯ เสร็จภายใน ${n.turnaround} สำหรับลูกค้าใน${loc.nameTh} มีบริการรับ-ส่งเอกสารถึงพื้นที่ + ทำ Apostille/MFA ต่อได้ทันที`,
      },
      {
        q: `ใช้ ${n.nameEn} กับประเทศไหนได้บ้าง?`,
        a: `Notarial Act ที่ออกในไทยใช้ได้ทั่วโลกหลังผ่าน Legalization ขั้นต่อ — สำหรับประเทศสมาชิก Hague ใช้ Apostille ของกระทรวงการต่างประเทศ (มีผลตั้งแต่ 13 ธ.ค. 2566) สำหรับประเทศนอก Hague ใช้รับรองสถานทูตปลายทาง`,
      },
      {
        q: `ลูกค้า${loc.nameTh}ส่งเอกสารยังไง?`,
        a: `3 ทาง — (1) สแกน/ถ่ายรูปส่ง LINE @nycli (รับใบเสนอราคาใน 15 นาที), (2) Walk-in สำนักงานใหญ่ลาดพร้าว 95 หรือสาขาขอนแก่น/อุดรธานี/หนองคาย, (3) ทนายเดินทางไปหา (+฿1,500 ในกทม.) — เอกสารตัวจริงต้องเซ็นต่อหน้าทนาย`,
      },
      {
        q: `${n.nameTh} ต้องเตรียมเอกสารอะไร?`,
        a: `เอกสารหลัก: ${n.required.join(" · ")} — ลูกค้าใน${loc.nameTh}สามารถส่งสำเนาให้ตรวจก่อนได้ ทีมจะแจ้งรายการที่ขาดและจุดที่ต้องแก้ก่อนนัดเซ็น`,
      },
    ];

    const rating = aggregateRatingNode(`notary/${n.slug}/${loc.slug}`);

    return {
      meta: [
        { title: ctaTitle(title, "/notary/$act/in/$location", "th") },
        { name: "description", content: ctaDesc(desc, "/notary/$act/in/$location", "th") },
        {
          name: "keywords",
          content: [
            `${n.nameTh} ${loc.nameTh}`,
            `${n.nameEn} ${loc.nameEn}`,
            `Notary ${loc.nameTh}`,
            `ทนาย Notary ${loc.nameTh}`,
            `${n.nameTh} ราคา`,
            `${n.nameTh} ใกล้ฉัน`,
          ].join(", "),
        },
        { property: "og:title", content: ctaTitle(title, "/notary/$act/in/$location", "th") },
        { property: "og:description", content: ctaDesc(desc, "/notary/$act/in/$location", "th") },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "th_TH" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: ctaTitle(title, "/notary/$act/in/$location", "th") },
        { name: "twitter:description", content: desc },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/notary/${n.slug}/in/${loc.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: `${n.nameEn} — ${loc.nameEn}`,
              alternateName: `${n.nameTh} · ${loc.nameTh}`,
              serviceType: "Notary Public",
              areaServed: {
                "@type": "Place",
                name: loc.nameTh,
                ...(loc.lat && loc.lng
                  ? { geo: { "@type": "GeoCoordinates", latitude: loc.lat, longitude: loc.lng } }
                  : {}),
              },
              provider: {
                "@type": "LegalService",
                "@id": `${SITE}/#org`,
                name: "NYC Legal & Notary Service",
                url: SITE,
                telephone: "+66832494999",
                address: {
                  "@type": "PostalAddress",
                  addressCountry: "TH",
                  addressLocality: loc.nameTh,
                },
              },
              offers: {
                "@type": "Offer",
                price: n.priceFromTh,
                priceCurrency: "THB",
                availability: "https://schema.org/InStock",
              },
              aggregateRating: rating,
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "@id": `${url}#faq`,
              mainEntity: faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "HowTo",
              "@id": `${url}#howto`,
              name: `วิธีทำ ${n.nameTh} สำหรับลูกค้า${loc.nameTh}`,
              totalTime: "PT1D",
              estimatedCost: {
                "@type": "MonetaryAmount",
                currency: "THB",
                value: String(n.priceFromTh),
              },
              step: [
                {
                  "@type": "HowToStep",
                  position: 1,
                  name: "ส่งคำขอ",
                  text: `ลูกค้า${loc.nameTh}ส่งภาพเอกสาร + คำขอผ่าน LINE @nycli — ทีมประเมินใน 15-30 นาที`,
                },
                {
                  "@type": "HowToStep",
                  position: 2,
                  name: "ยืนยันราคา + นัดเซ็น",
                  text: "ยืนยันค่าบริการ จัดเวลานัดเข้าสำนักงาน หรือให้ทนายเดินทางไปหา",
                },
                {
                  "@type": "HowToStep",
                  position: 3,
                  name: "เซ็นต่อหน้าทนาย",
                  text: "ลูกค้าเซ็นเอกสารต่อหน้าทนาย Notarial Services Attorney + ตรวจ ID",
                },
                {
                  "@type": "HowToStep",
                  position: 4,
                  name: "ตราประทับ Notary",
                  text: "ทนายลงนาม + ตราประทับ Notary + ออก Notarial Certificate",
                },
                {
                  "@type": "HowToStep",
                  position: 5,
                  name: "ต่อ Apostille/Embassy (ถ้าต้อง)",
                  text: "บริการต่อยอดที่กระทรวงการต่างประเทศ + สถานทูตปลายทาง (เพิ่ม 1-5 วัน)",
                },
                {
                  "@type": "HowToStep",
                  position: 6,
                  name: "ส่งคืน",
                  text: `รับเองที่สำนักงาน หรือ EMS/Grab/Messenger ส่งถึง${loc.nameTh}`,
                },
              ],
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "Notary Public", item: `${SITE}/notary` },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: n.nameTh,
                  item: `${SITE}/notary/${n.slug}`,
                },
                { "@type": "ListItem", position: 4, name: loc.nameTh, item: url },
              ],
            }),
          },
          ...reviewNodes(url, `${url}#service`, 3).map((r) => ({
            type: "application/ld+json" as const,
            children: JSON.stringify(r),
          })),
        ]),
      ],
    };
  },
  component: NotaryActLocationPage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">ไม่พบหน้า</h1>
      <Link to="/notary" className="mt-4 inline-block text-gold underline">
        กลับ Notary hub
      </Link>
    </main>
  ),
});
